import React, { useState, useEffect, useRef, memo } from 'react';
import EmojiItem from './EmojiItem';
import { translations } from '../translations';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiLoader } from 'react-icons/fi';

const EmojiGrid = ({ 
  emojis, 
  skinTone, 
  onSingleCopy, 
  onAddToTray, 
  lang, 
  isSearchMode = false, 
  onCategoryClick,
  scrollContainerRef // 接收捲動容器 Ref
}) => {
  const t = translations[lang] || translations['zh-TW'];
  
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const ITEMS_PER_PAGE = isMobile ? 80 : 160; 
  
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const observerTarget = useRef(null);

  // 當資料集改變時，重置計數
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [emojis, ITEMS_PER_PAGE]);

  // 核心修正：綁定正確的滾動容器作為 IntersectionObserver 的根
  useEffect(() => {
    if (!observerTarget.current || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < emojis.length) {
          // 使用微任務確保渲染流暢
          requestAnimationFrame(() => {
            setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
          });
        }
      },
      { 
        root: scrollContainerRef.current, // 重要：指定捲動容器為 root
        threshold: 0.1, 
        rootMargin: '600px' // 手機版加大預加載範圍，減少卡頓感
      }
    );

    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [visibleCount, emojis.length, ITEMS_PER_PAGE, scrollContainerRef]);

  // 搜尋模式：維持分類顯示
  if (isSearchMode) {
    const grouped = emojis.reduce((acc, emoji) => {
      if (!acc[emoji.category]) acc[emoji.category] = [];
      acc[emoji.category].push(emoji);
      return acc;
    }, {});

    return (
      <div className="space-y-12 pb-20">
        {Object.entries(grouped).map(([category, items]) => (
          <section key={category} style={{ contentVisibility: 'auto' }}>
            <button 
              onClick={() => onCategoryClick && onCategoryClick(category)}
              className="group flex items-center gap-3 mb-6 w-full text-left hover:bg-slate-100/50 p-2 -ml-2 rounded-xl transition-all"
            >
              <div className="w-1.5 h-6 bg-indigo-500 rounded-full group-hover:h-8 transition-all" />
              <h3 className="text-slate-500 font-black text-sm md:text-base tracking-widest uppercase flex items-center gap-2">
                {t.categories[category] || category}
                <span className="bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full text-[10px]">
                  {items.length}
                </span>
              </h3>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-xs text-indigo-500 font-bold ml-auto pr-2">
                {t.viewMore} <SafeIcon icon={FiArrowRight} />
              </div>
            </button>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3 md:gap-4">
              {items.map((emoji, idx) => (
                <EmojiItem 
                  key={`${emoji.char}-${idx}`} 
                  emoji={emoji} 
                  skinTone={skinTone} 
                  onSingleCopy={onSingleCopy} 
                  onAddToTray={onAddToTray}
                  lang={lang}
                  disableAnimation={true}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  // 正常模式：分段加載
  const visibleEmojis = emojis.slice(0, visibleCount);

  return (
    <div className="space-y-8 pb-32">
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3 md:gap-4">
        {visibleEmojis.map((emoji, idx) => (
          <EmojiItem 
            key={`${emoji.char}-${idx}`} 
            emoji={emoji} 
            skinTone={skinTone} 
            onSingleCopy={onSingleCopy} 
            onAddToTray={onAddToTray} 
            lang={lang}
            disableAnimation={emojis.length > 100} 
          />
        ))}
      </div>
      
      {/* 監聽目標：確保始終渲染，以免觀察器失效 */}
      <div 
        ref={observerTarget} 
        className={`w-full py-20 flex flex-col items-center justify-center gap-4 text-slate-400 ${visibleCount >= emojis.length ? 'opacity-0 h-0 py-0' : 'opacity-100'}`}
      >
        <FiLoader className="text-2xl animate-spin text-indigo-500" />
        <span className="text-[10px] font-black uppercase tracking-[0.3em]">Loading Engine...</span>
      </div>
    </div>
  );
};

export default memo(EmojiGrid);